import {
  MAXIMUM_SAVED_VIEW_NAME_LENGTH,
  savedViewSchema,
  type ReportFilterStateV1,
  type SavedView,
  type UpdateSavedViewRequest,
} from "@meta-video/contracts";
import {
  Alert,
  Button,
  Group,
  Menu,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useMergedRef } from "@mantine/hooks";
import { useEffect, useRef, useState, type Ref } from "react";

import { returnFocus } from "../../app/a11y.js";
import { SessionExpiredError } from "../../shared/api/client.js";
import { useProductAnalytics } from "../../shared/analytics/useProductAnalytics.js";
import {
  ShareLinkButton,
  type ShareLinkResult,
} from "../workspace/ShareLinkButton.js";
import { deleteSavedView, updateSavedView } from "./api.js";

export interface SavedViewActionsApi {
  readonly deleteSavedView: (viewId: string) => Promise<void>;
  readonly updateSavedView: (
    viewId: string,
    input: UpdateSavedViewRequest,
  ) => Promise<SavedView>;
}

const defaultApi: SavedViewActionsApi = { deleteSavedView, updateSavedView };
type PendingMutation = "default" | "delete" | "rename" | "update" | null;
type MutationContext = Readonly<{
  accessRole: SavedView["accessRole"];
  filtersKey: string;
  revision: number;
  viewId: string;
  workspaceKey: string | null;
}>;
type ConflictMutation = Readonly<{
  current: SavedView;
  filters?: ReportFilterStateV1;
  kind: "rename" | "update";
  name?: string;
  originalContext: MutationContext;
  viewId: string;
}>;

export interface SavedViewActionsProps {
  readonly api?: SavedViewActionsApi;
  readonly currentFilters?: ReportFilterStateV1;
  readonly filterRevision?: number;
  readonly includePrimaryActions?: boolean;
  readonly isAssociated: boolean;
  readonly isDirty: boolean;
  readonly isPartial: boolean;
  readonly isOpening?: boolean;
  readonly onChanged: (
    view: SavedView,
    expectedWorkspaceKey: string,
  ) => void | Promise<void>;
  readonly onDeleted: (
    viewId: string,
    expectedWorkspaceKey: string | null,
  ) => void | Promise<void>;
  readonly onManageCollaborators: () => void;
  readonly onMetadataChanged: (view?: SavedView) => void | Promise<void>;
  readonly onOpen: () => void;
  readonly onSessionExpired: () => void;
  readonly shortLinkUrl?: string;
  readonly triggerRef?: Ref<HTMLButtonElement>;
  readonly view: SavedView;
  readonly workspaceKey: string | null;
}

function revisionConflict(error: unknown): SavedView | null {
  if (
    typeof error !== "object"
    || error === null
    || !("code" in error)
    || error.code !== "SAVED_VIEW_REVISION_CONFLICT"
    || !("current" in error)
  ) return null;
  const parsed = savedViewSchema.safeParse(error.current);
  return parsed.success ? parsed.data : null;
}

function shouldReconcile(error: unknown): boolean {
  return typeof error === "object"
    && error !== null
    && "code" in error
    && (
      error.code === "SAVED_VIEW_NOT_FOUND"
      || error.code === "SAVED_VIEW_CONFLICT"
    );
}

function mutationErrorMessage(error: unknown): string {
  return shouldReconcile(error)
    ? "This Saved View is no longer available or your access changed."
    : "The Saved View could not be changed. Try again.";
}

export function SavedViewActions({
  api = defaultApi,
  currentFilters,
  filterRevision,
  includePrimaryActions = false,
  isAssociated,
  isDirty,
  isPartial,
  isOpening = false,
  onChanged,
  onDeleted,
  onManageCollaborators,
  onMetadataChanged,
  onOpen,
  onSessionExpired,
  shortLinkUrl,
  triggerRef,
  view,
  workspaceKey,
}: SavedViewActionsProps) {
  const analytics = useProductAnalytics();
  const [conflict, setConflict] = useState<ConflictMutation | null>(null);
  const [partialContext, setPartialContext] = useState<MutationContext | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmPartial, setConfirmPartial] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingMutation>(null);
  const [renameName, setRenameName] = useState("");
  const [renameTarget, setRenameTarget] = useState<SavedView | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [shareResult, setShareResult] = useState<ShareLinkResult | null>(null);
  const actionsTriggerRef = useRef<HTMLButtonElement>(null);
  const mergedTriggerRef = useMergedRef(actionsTriggerRef, triggerRef);
  const context: MutationContext = {
    accessRole: view.accessRole,
    filtersKey: JSON.stringify(currentFilters ?? null),
    revision: filterRevision ?? view.revision,
    viewId: view.id,
    workspaceKey,
  };
  const currentContext = useRef(context);
  currentContext.current = context;
  const isOwner = view.accessRole === "OWNER";
  const canEdit = isOwner || view.accessRole === "EDIT";

  useEffect(() => {
    setConflict(null);
    setConfirmDelete(false);
    setConfirmPartial(false);
    setPartialContext(null);
    setError(null);
    setRenameName("");
    setRenameTarget(null);
    setRenaming(false);
    setShareResult(null);
  }, [view.id]);

  useEffect(() => {
    if (canEdit) return;
    setConflict(null);
    setConfirmDelete(false);
    setConfirmPartial(false);
    setPartialContext(null);
    setRenameTarget(null);
    setRenaming(false);
  }, [canEdit]);

  useEffect(() => {
    if (isOwner) return;
    setConfirmDelete(false);
  }, [isOwner]);

  useEffect(() => {
    if (
      !confirmPartial
      || partialContext === null
      || (
        partialContext.viewId === context.viewId
        && partialContext.accessRole === context.accessRole
        && partialContext.revision === context.revision
        && partialContext.filtersKey === context.filtersKey
        && partialContext.workspaceKey === context.workspaceKey
      )
    ) return;
    setConfirmPartial(false);
    setPartialContext(null);
  }, [
    confirmPartial,
    context.accessRole,
    context.filtersKey,
    context.revision,
    context.viewId,
    context.workspaceKey,
    partialContext,
  ]);

  useEffect(() => {
    if (
      conflict !== null
      && (
        context.viewId !== conflict.originalContext.viewId
        || context.accessRole !== conflict.originalContext.accessRole
        || context.filtersKey !== conflict.originalContext.filtersKey
        || context.workspaceKey !== conflict.originalContext.workspaceKey
      )
    ) {
      setConflict(null);
      void onMetadataChanged();
    }
  }, [
    conflict,
    context.accessRole,
    context.filtersKey,
    context.viewId,
    context.workspaceKey,
    onMetadataChanged,
  ]);

  const run = async (
    kind: Exclude<PendingMutation, null>,
    request: () => Promise<void>,
  ) => {
    setError(null);
    setPending(kind);
    try {
      await request();
    } catch (caught) {
      if (caught instanceof SessionExpiredError) {
        onSessionExpired();
        return;
      }
      if (shouldReconcile(caught)) await onMetadataChanged();
      setError(mutationErrorMessage(caught));
    } finally {
      setPending(null);
    }
  };

  const submitFilters = (
    operationView: SavedView,
    filters: ReportFilterStateV1,
    expectedRevision: number,
    originalContext: MutationContext = { ...currentContext.current },
  ) => {
    const submitted = originalContext;
    void run("update", async () => {
      try {
        const changed = await api.updateSavedView(operationView.id, {
          expectedRevision,
          filters,
        });
        setConfirmPartial(false);
        setConflict(null);
        if (
          currentContext.current.viewId === submitted.viewId
          && currentContext.current.accessRole === submitted.accessRole
          && currentContext.current.revision === submitted.revision
          && currentContext.current.filtersKey === submitted.filtersKey
        ) {
          if (submitted.workspaceKey !== null) {
            await onChanged(changed, submitted.workspaceKey);
          } else {
            await onMetadataChanged(changed);
          }
        } else {
          await onMetadataChanged(changed);
        }
        analytics.savedViewAction("update");
      } catch (caught) {
        const current = revisionConflict(caught);
        if (current !== null && current.id === operationView.id) {
          if (
            currentContext.current.viewId === operationView.id
            && currentContext.current.accessRole === submitted.accessRole
            && currentContext.current.filtersKey === submitted.filtersKey
          ) {
            setConfirmPartial(false);
            setPartialContext(null);
            setConflict({
              current,
              filters,
              kind: "update",
              originalContext: submitted,
              viewId: operationView.id,
            });
          } else {
            await onMetadataChanged(current);
          }
          return;
        }
        throw caught;
      }
    });
  };

  const submitRename = (
    operationView: SavedView,
    name: string,
    expectedRevision: number,
  ) => {
    const submitted = { ...currentContext.current };
    void run("rename", async () => {
      try {
        const changed = await api.updateSavedView(operationView.id, {
          expectedRevision,
          name,
        });
        setConflict(null);
        setRenaming(false);
        if (
          currentContext.current.viewId === submitted.viewId
          && currentContext.current.accessRole === submitted.accessRole
          && currentContext.current.revision === submitted.revision
        ) await onMetadataChanged(changed);
        else await onMetadataChanged();
        analytics.savedViewAction("rename");
      } catch (caught) {
        const current = revisionConflict(caught);
        if (current !== null && current.id === operationView.id) {
          if (
            currentContext.current.viewId === operationView.id
            && currentContext.current.accessRole === submitted.accessRole
          ) {
            setRenaming(false);
            setConflict({
              current,
              kind: "rename",
              name,
              originalContext: submitted,
              viewId: operationView.id,
            });
          } else {
            await onMetadataChanged(current);
          }
          return;
        }
        throw caught;
      }
    });
  };

  if (!canEdit && !includePrimaryActions) return null;

  return (
    <>
      <Menu position="bottom-end" shadow="md" width={220}>
        <Menu.Target>
          <Button
            aria-label={isOpening ? `Opening ${view.name}` : `Actions for ${view.name}`}
            disabled={pending !== null || isOpening}
            size="compact-sm"
            ref={mergedTriggerRef}
            variant="subtle"
          >{isOpening ? "Opening…" : "Actions"}</Button>
        </Menu.Target>
        <Menu.Dropdown>
          {includePrimaryActions ? <Menu.Item onClick={onOpen}>Open</Menu.Item> : null}
          {includePrimaryActions && shortLinkUrl ? (
            <ShareLinkButton
              asMenuItem
              label="Copy link"
              onActivate={() => setShareResult(null)}
              onResult={setShareResult}
              renderFeedback={false}
              url={shortLinkUrl}
            />
          ) : null}
          {canEdit && isAssociated && isDirty && currentFilters ? (
            <Menu.Item onClick={() => {
              if (isPartial) {
                setPartialContext({ ...context });
                setConfirmPartial(true);
              }
              else submitFilters(view, currentFilters, filterRevision ?? view.revision);
            }}>Update filters</Menu.Item>
          ) : null}
          {canEdit ? (
            <Menu.Item onClick={() => {
              setRenameName(view.name);
              setRenameTarget(view);
              setRenaming(true);
            }}>Rename</Menu.Item>
          ) : null}
          {isOwner && !view.isDefault ? (
            <Menu.Item onClick={() => {
              const operationView = view;
              void run("default", async () => {
                const changed = await api.updateSavedView(operationView.id, { isDefault: true });
                await onMetadataChanged(changed);
                analytics.savedViewAction("set-default");
              });
            }}>Set as default</Menu.Item>
          ) : null}
          {isOwner ? (
            <Menu.Item onClick={onManageCollaborators}>
              Manage collaborators
            </Menu.Item>
          ) : null}
          {isOwner ? (
            <Menu.Item color="red" onClick={() => setConfirmDelete(true)}>Delete</Menu.Item>
          ) : null}
        </Menu.Dropdown>
      </Menu>
      <span
        aria-label={isOpening ? `Opening ${view.name}` : undefined}
        aria-live="polite"
        className="visually-hidden"
        role="status"
      >
        {isOpening ? `Opening ${view.name}…` : ""}
      </span>

      {shareResult?.status === "fallback" ? (
        <Stack className="share-link-fallback" gap="xs">
          <Text size="sm">Clipboard access is unavailable. Copy this link:</Text>
          <TextInput
            aria-label="Copy this link"
            onFocus={(event) => event.currentTarget.select()}
            readOnly
            value={shareResult.url}
          />
        </Stack>
      ) : shareResult?.status === "invalid" ? (
        <Text size="sm">This link could not be prepared.</Text>
      ) : null}
      <span aria-live="polite" className="visually-hidden" role="status">
        {shareResult?.status === "copied"
          ? "Link copied."
          : shareResult?.status === "fallback"
            ? "Clipboard unavailable. Copy the link below."
            : shareResult?.status === "invalid"
              ? "Link unavailable."
              : ""}
      </span>

      {error && !renaming && !confirmDelete && !confirmPartial && conflict === null ? (
        <Alert color="red" role="alert">{error}</Alert>
      ) : null}

      <Modal
        onClose={() => setRenaming(false)}
        opened={renaming}
        returnFocus={conflict === null}
        title={`Rename ${renameTarget?.name ?? view.name}`}
      >
        <form onSubmit={(event) => {
          event.preventDefault();
          const name = renameName.trim();
          if (name !== "" && renameTarget) {
            submitRename(renameTarget, name, renameTarget.revision);
          }
        }}>
          <Stack gap="sm">
            {error ? <Alert color="red" role="alert">{error}</Alert> : null}
            <TextInput
              autoFocus
              disabled={pending === "rename"}
              label="New name"
              maxLength={MAXIMUM_SAVED_VIEW_NAME_LENGTH}
              onChange={(event) => setRenameName(event.currentTarget.value)}
              value={renameName}
            />
            <Group justify="flex-end">
              <Button disabled={pending !== null} onClick={() => setRenaming(false)} type="button" variant="subtle">Cancel</Button>
              <Button disabled={renameName.trim() === "" || pending !== null} loading={pending === "rename"} type="submit">Rename</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal onClose={() => setConfirmDelete(false)} opened={confirmDelete} title={`Delete ${view.name}?`}>
        <Stack gap="sm">
          {error ? <Alert color="red" role="alert">{error}</Alert> : null}
          <Text>This removes the Saved View for everyone who can currently access it.</Text>
          <Group justify="flex-end">
            <Button disabled={pending !== null} onClick={() => setConfirmDelete(false)} variant="subtle">Cancel</Button>
            <Button
              color="red"
              disabled={pending !== null}
              loading={pending === "delete"}
              onClick={() => {
                const viewId = view.id;
                const operationWorkspaceKey = workspaceKey;
                const submitted = { ...currentContext.current };
                void run("delete", async () => {
                  await api.deleteSavedView(viewId);
                  setConfirmDelete(false);
                  if (
                    currentContext.current.viewId === submitted.viewId
                    && currentContext.current.accessRole === submitted.accessRole
                  ) await onDeleted(viewId, operationWorkspaceKey);
                  else await onMetadataChanged();
                  analytics.savedViewAction("delete");
                });
              }}
            >Delete Saved View</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        onClose={() => setConfirmPartial(false)}
        opened={confirmPartial}
        returnFocus={conflict === null}
        title="Confirm reduced-scope update"
      >
        <Stack gap="sm">
          {error ? <Alert color="red" role="alert">{error}</Alert> : null}
          <Text>This Saved View is only partially available. Updating it will replace its stored filters with your reduced permitted scope.</Text>
          <Group justify="flex-end">
            <Button disabled={pending !== null} onClick={() => setConfirmPartial(false)} variant="subtle">Keep Saved View unchanged</Button>
            <Button
              color="orange"
              disabled={pending !== null || currentFilters === undefined}
              onClick={() => {
                if (currentFilters && partialContext) submitFilters(
                  view,
                  currentFilters,
                  partialContext.revision,
                  partialContext,
                );
              }}
            >Confirm partial overwrite</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        onClose={() => setConflict(null)}
        onExitTransitionEnd={() => returnFocus(actionsTriggerRef.current)}
        opened={conflict !== null}
        returnFocus={false}
        title="Saved View changed"
      >
        {conflict ? (
          <Stack gap="sm">
            {error ? <Alert color="red" role="alert">{error}</Alert> : null}
            <Alert color="orange" role="alert">
              <Text fw={600}>{conflict.current.name}</Text>
              <Text size="sm">Revision {conflict.current.revision} is now saved. Your change was not applied.</Text>
            </Alert>
            <Group justify="flex-end">
              <Button
                data-autofocus
                disabled={pending !== null}
                onClick={() => {
                  const current = conflict.current;
                  setConflict(null);
                  if (conflict.kind === "rename") {
                    void onMetadataChanged(current);
                  } else if (
                    conflict.originalContext.workspaceKey !== null
                    && currentContext.current.filtersKey === conflict.originalContext.filtersKey
                    && currentContext.current.accessRole === conflict.originalContext.accessRole
                    && currentContext.current.viewId === conflict.originalContext.viewId
                    && currentContext.current.workspaceKey === conflict.originalContext.workspaceKey
                  ) {
                    void onChanged(current, conflict.originalContext.workspaceKey);
                  } else {
                    void onMetadataChanged(current);
                  }
                }}
                variant="subtle"
              >Use current saved state</Button>
              <Button
                disabled={pending !== null}
                loading={pending === conflict.kind}
                onClick={() => {
                  if (currentContext.current.viewId !== conflict.viewId) {
                    setConflict(null);
                    void onMetadataChanged();
                    return;
                  }
                  if (
                    currentContext.current.filtersKey !== conflict.originalContext.filtersKey
                    || currentContext.current.accessRole !== conflict.originalContext.accessRole
                    || currentContext.current.workspaceKey !== conflict.originalContext.workspaceKey
                  ) {
                    setConflict(null);
                    void onMetadataChanged();
                    return;
                  }
                  if (conflict.kind === "update" && conflict.filters) {
                    submitFilters(
                      conflict.current,
                      conflict.filters,
                      conflict.current.revision,
                      conflict.originalContext,
                    );
                  } else if (conflict.kind === "rename" && conflict.name) {
                    submitRename(conflict.current, conflict.name, conflict.current.revision);
                  }
                }}
              >{conflict.kind === "update" ? "Reapply my filters" : "Reapply my name"}</Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </>
  );
}
